import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HttpClientXsrfModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { UserIdleModule } from 'angular-user-idle';
import { NgxPaginationModule } from 'ngx-pagination';
import { DatePipe, CurrencyPipe } from '@angular/common';
import { SharedModule } from './shared/shared.module';
import { OrderModule } from 'ngx-order-pipe';
import { AuditLogModule } from './audit-log/audit-log.module';
import { SetupModule } from './setup/setup.module';
import { DashboardModule } from './dashboard/dashboard.module';
import { AppHeaderModule } from './app-header/app-header.module';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { NotfoundComponent } from './notfound/notfound.component';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { SearchModule } from './search-module/search.module';
import { JwtInterceptor } from '@interceptors';
import { AuthenticationService } from './services/authentication.service';
import { MatNativeDateModule } from '@angular/material/core';
import { NotificationModule } from '@app/notification-module/notification.module';
import { ComponentOAuthComponent } from './component-o-auth/component-o-auth.component';
import { ComponentOAuthLoginComponent } from './component-o-auth-login/component-o-auth-login.component';
import { AssessmentOrderComponent } from './assessments/assessment-order/assessment-order.component';
import { EntityModule } from './entity/entity.module';
import { InlineAssessmentComponent } from './assessments-investigation/inline-assessmentmain/inline-assessment.component';
import { GuestBiometricComponent } from './guest-biometric/guest-biometric.component';
import { MatRadioModule } from '@angular/material/radio';
import { CandidateDropdownComponent } from './candidate-dropdown/candidate-dropdown.component';
import { EmployeesComponent } from './employees/employees.component';
import { BgIndexMappingComponent } from './bg-index-mapping/bg-index-mapping.component';
import { SsoComponent } from './sso/sso.component';
import { EmailComponent } from './email/email.component';
import { ExclusionComponent } from './exclusion/exclusion.component';
import { AddExclusionComponent } from './exclusion/add-exclusion/add-exclusion.component';
import { EditExclusionComponent } from './exclusion/edit-exclusion/edit-exclusion.component';
import { ComplianceLayoutComponent } from './compliance-layout/compliance-layout.component';
import { CashCageComplianceLayoutComponent } from './compliance-layout/cashcage/cashcage-compliance-layout.component';
import { SecurityComplianceLayoutComponent } from './compliance-layout/security/security-compliance-layout.component';
import { CommonTableComponent } from './common-table/common-table.component';
import { AgeVerificationComponent } from './compliance-layout/cashcage/age-verification.component';
import { VddOnboardingService } from './vdd-onboarding-page/services/vdd-onboarding.service';
import { PatronsRoutingModule } from './patrons-routing/patrons-routing.module';
import { BackgroundChecksModule } from './background-checks/background-checks.module';
import { PatronSearchPageModule } from './patron-search-page';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import {
  RepeatSectionTypeComponent,
  SectionWrapperComponent,
  FileUploadWithTemplateComponent,
  FileUploadMultiComponent,
} from './shared/formly';

export function initialize(authService: AuthenticationService): () => Promise<any> {
  return () => {
    return firstValueFrom(authService.getCurrentUser());
  };
}

@NgModule({
  declarations: [
    AppComponent,
    NotfoundComponent,
    ComponentOAuthComponent,
    ComponentOAuthLoginComponent,
    AssessmentOrderComponent,
    InlineAssessmentComponent,
    GuestBiometricComponent,
    CandidateDropdownComponent,
    EmployeesComponent,
    BgIndexMappingComponent,
    SsoComponent,
    EmailComponent,
    ExclusionComponent,
    AddExclusionComponent,
    EditExclusionComponent,
    ComplianceLayoutComponent,
    CashCageComplianceLayoutComponent,
    SecurityComplianceLayoutComponent,
    CommonTableComponent,
    AgeVerificationComponent,
  ],
  imports: [
    NotificationModule,
    MatNativeDateModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    NgxPaginationModule,
    NgSelectModule,
    EntityModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    HttpClientXsrfModule,
    SharedModule,
    AuditLogModule,
    FormsModule,
    SetupModule,
    DashboardModule,
    AppHeaderModule,
    UserIdleModule.forRoot({ idle: 1800, timeout: 1800, ping: 1800 }),
    OrderModule,
    MatExpansionModule,
    MatDialogModule,
    MatTableModule,
    MatTabsModule,
    SearchModule,
    MatRadioModule,
    TranslateModule.forRoot(),
    PatronsRoutingModule,
    BackgroundChecksModule,
    PatronSearchPageModule,
    FormlyModule.forRoot({
      types: [
        { name: 'repeat', component: RepeatSectionTypeComponent },
        { name: 'file-upload-with-template', component: FileUploadWithTemplateComponent },
        { name: 'file-upload-multi', component: FileUploadMultiComponent },
      ],
      wrappers: [{ name: 'section', component: SectionWrapperComponent }],
      extras: {
        resetFieldOnHide: false,
      },
    }),
    FormlyMaterialModule,
  ],

  providers: [
    ...provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json',
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initialize,
      deps: [AuthenticationService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    DatePipe,
    CurrencyPipe,
    VddOnboardingService,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
